import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { onboardingQuestions, getChallengeQuestion, OnboardingStep, OnboardingQuestion } from '@/data/onboardingData';
import { ArrowLeft, ArrowRight, Sparkles, CheckCircle2, LogIn } from 'lucide-react';
import { usePersonalizedPath } from '@/hooks/usePersonalizedPath';
import { createPath } from '@/services/userPathService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getChallengeOption } from '@/data/challengeOptions';
import { hasValidLicenseInStorage } from '@/services/licenseService';

const stepOrder: OnboardingStep[] = ['role', 'focus', 'challenge', 'style', 'summary'];

const NewOnboardingFlow: React.FC = () => {
    const [currentStep, setCurrentStep] = useState<OnboardingStep>('role');
    const [selections, setSelections] = useState({
        role: '',
        focus: '',
        challenge: '',
        style: ''
    });
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();
    const { refetch } = usePersonalizedPath();
    const { user } = useAuth();

    // License guard: new users must have a valid license to proceed
    useEffect(() => {
        // If user is already authenticated, they're grandfathered in
        if (user) return;

        // If no valid license in storage, redirect to redeem page
        if (!hasValidLicenseInStorage()) {
            navigate('/redeem');
        }
    }, [user, navigate]);

    const stepIndex = useMemo(() => stepOrder.indexOf(currentStep), [currentStep]);
    const totalQuestions = 4; // role, focus, challenge, style

    const goBack = () => {
        const prevIndex = Math.max(0, stepIndex - 1);
        setCurrentStep(stepOrder[prevIndex]);
    };

    const goNext = (nextStep: OnboardingStep) => {
        setCurrentStep(nextStep);
    };

    const handleSelect = async (optionId: string) => {
        const newSelections = { ...selections, [currentStep]: optionId };
        setSelections(newSelections);

        if (currentStep === 'role') return goNext('focus');
        if (currentStep === 'focus') return goNext('challenge');
        if (currentStep === 'challenge') return goNext('style');
        if (currentStep === 'style') return goNext('summary');
    };

    const handleCompletion = async (finalSelections: typeof selections) => {
        setSaving(true);

        if (!user) {
            localStorage.setItem('lyra-onboarding-temp', JSON.stringify(finalSelections));
            navigate('/auth?signup=true');
            setSaving(false);
            return;
        }

        try {
            await createPath(finalSelections.focus, finalSelections.challenge, {
                userId: user.id,
                style: finalSelections.style
            });
            await refetch();
            navigate('/micro-dashboard');
        } catch (error) {
            console.error("Error generating path:", error);
            navigate('/micro-dashboard');
        } finally {
            setSaving(false);
        }
    };

    const renderStep = () => {
        if (currentStep === 'summary') {
            // Get the challenge title from the challenge options
            const challengeOption = getChallengeOption(selections.challenge);
            const challengeTitle = challengeOption?.title || selections.challenge;

            return (
                <motion.div
                    key="summary"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    className="space-y-6"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.3em] text-slate-400">Review</p>
                            <h1 className="text-3xl font-bold text-slate-900">Save this path</h1>
                            <p className="text-lg text-slate-600">Create an account to lock in your picks and start your first micro.</p>
                        </div>
                        <div className="hidden sm:flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 px-4 py-2 text-xs uppercase tracking-[0.2em]">
                            <CheckCircle2 className="w-4 h-4" />
                            Ready in 5 minutes
                        </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        {[
                            { label: 'Role', value: selections.role || 'Not set' },
                            { label: 'Focus', value: selections.focus || 'Not set' },
                            { label: 'Challenge', value: challengeTitle },
                            { label: 'Start style', value: selections.style || 'Not set' },
                        ].map((item) => (
                            <div key={item.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                                <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500">{item.label}</p>
                                <p className="text-base font-semibold text-slate-900 mt-1">{item.value.replace(/_/g, ' ')}</p>
                            </div>
                        ))}
                    </div>

                    <div className="space-y-3">
                        <button
                            onClick={() => handleCompletion(selections)}
                            disabled={saving || !selections.role || !selections.focus || !selections.challenge || !selections.style}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 text-white py-4 text-lg font-semibold shadow-lg hover:bg-slate-800 transition disabled:opacity-60"
                        >
                            {saving ? 'Saving your path…' : user ? 'Start with my path' : 'Create account to start'}
                            {!saving && <ArrowRight className="w-5 h-5" />}
                        </button>
                        {!user && (
                            <p className="text-sm text-slate-500 text-center">
                                We’ll save these picks to your account so you can jump straight into your first micro lesson.
                            </p>
                        )}
                    </div>
                </motion.div>
            );
        }

        if (currentStep === 'generating') {
            return (
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center space-y-6 py-12"
                >
                    <div className="relative w-24 h-24 mx-auto">
                        <div className="absolute inset-0 bg-slate-100 rounded-full animate-ping opacity-75"></div>
                        <div className="relative bg-white p-6 rounded-full shadow-xl border border-slate-100">
                            <Sparkles className="w-12 h-12 text-slate-900 animate-pulse" />
                        </div>
                    </div>
                    <div>
                        <h2 className="text-2xl font-semibold text-slate-900">Building your personalized path...</h2>
                        <p className="text-slate-500 mt-2">Analyzing your goals and matching with our best tools.</p>
                    </div>
                </motion.div>
            );
        }

        let question: OnboardingQuestion;
        if (currentStep === 'challenge') {
            question = getChallengeQuestion(selections.focus);
        } else {
            question = onboardingQuestions[currentStep as keyof typeof onboardingQuestions];
        }

        const questionNumber = Math.min(stepIndex + 1, totalQuestions);

        return (
            <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
            >
                <div className="flex items-start justify-between gap-4">
                    <div className="space-y-2">
                        <p className="text-xs font-bold uppercase tracking-[0.3em] text-slate-400">
                            Step {questionNumber} of {totalQuestions}
                        </p>
                        <h1 className="text-3xl font-bold text-slate-900">{question.title}</h1>
                        <p className="text-lg text-slate-600">{question.subtitle}</p>
                    </div>
                    <button
                        type="button"
                        onClick={goBack}
                        disabled={currentStep === 'role'}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-2 text-xs uppercase tracking-[0.2em] text-slate-600 hover:border-slate-400 disabled:opacity-50"
                        aria-label="Go back"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </button>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                    {question.options.map((option) => (
                        <button
                            key={option.id}
                            onClick={() => handleSelect(option.id)}
                            className="group relative p-6 text-left bg-white rounded-2xl border border-slate-200 shadow-sm hover:border-slate-900 hover:shadow-md transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400"
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="font-semibold text-slate-900 group-hover:text-slate-900 transition-colors">
                                        {option.label}
                                    </h3>
                                    <p className="text-sm text-slate-500 mt-1 group-hover:text-slate-600">
                                        {option.description}
                                    </p>
                                </div>
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-900">
                                    <ArrowRight className="w-5 h-5" />
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </motion.div>
        );
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto w-full space-y-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-slate-500">
                        <Sparkles className="w-4 h-4" />
                        Lyra builds your micro path
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate('/auth?signin=true')}
                        className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
                    >
                        <LogIn className="w-4 h-4" />
                        Returning? Sign in
                    </button>
                </div>
                <AnimatePresence mode="wait">
                    {renderStep()}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default NewOnboardingFlow;
